import { NotificationFragment } from "@graphql/notifications/Notifications.g";
import { useMemo } from "react";
import { useNavigate } from "react-router";

import { Clickable } from "@design-system/Clickable/Clickable";
import { Resource } from "@design-system/Resource/Resource";
import { Skeleton } from "@design-system/Skeleton/Skeleton";
import { Table } from "@design-system/Table/Table";
import { Tag } from "@design-system/Tag/Tag";
import { Text } from "@design-system/Text/Text";

import { routes } from "@pages/routes";
import { getNotificationTypeLabel } from "@utils/notificationTypes";

import classes from "./RecentAlertsCard.module.css";
import { useRecentAlertsQuery } from "./useRecentAlerts";

const DEFAULT_LIMIT = 8;

type Props = {
  clientId?: string;
  limit?: number;
};

export const RecentAlertsList = ({
  clientId,
  limit = DEFAULT_LIMIT,
}: Props) => {
  const navigate = useNavigate();
  const notificationsQuery = useRecentAlertsQuery(clientId, limit);
  const edges = notificationsQuery.data?.notifications?.edges;
  const isLoading = notificationsQuery.isLoading;

  const alerts = useMemo(() => {
    return (edges ?? [])
      .map((edge) => edge?.node)
      .filter((node): node is NotificationFragment => Boolean(node));
  }, [edges]);

  const handleAlertClick = (alert: NotificationFragment) => {
    if (clientId || !alert.client?.id) {
      navigate(
        clientId
          ? routes.portal.clients.client.notifications.$buildPath({
              params: { clientId },
            })
          : routes.portal.notifications.$path(),
        { viewTransition: true },
      );
      return;
    }

    navigate(
      routes.portal.clients.client.notifications.$buildPath({
        params: { clientId: alert.client.id },
      }),
      { viewTransition: true },
    );
  };

  if (isLoading) {
    return (
      <div className={classes.list}>
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className={classes.row}>
            <Skeleton width="40%" height={16} />
            <Skeleton width="30%" height={14} />
          </div>
        ))}
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <Text size={12} color="textSecondary">
        No recent alerts
      </Text>
    );
  }

  return (
    <div className={classes.list}>
      {alerts.map((alert) => {
        const mainApp =
          alert.application?.resourceGroupApplications?.primaryApplication ||
          alert.application;
        const productName =
          mainApp?.resource?.productName ?? "Unknown application";
        const signedLogoUrl =
          mainApp?.resource?.publicResource?.signedLogoUrl || "";
        const tenantName = clientId
          ? undefined
          : (alert.client?.name ?? alert.tenantDisplayName ?? undefined);

        return (
          <Clickable
            key={alert.id}
            onClick={() => handleAlertClick(alert)}
            stretch
          >
            <div className={classes.row}>
              <div className={classes.name}>
                <Resource
                  name={productName}
                  signedLogoUrl={signedLogoUrl}
                  subtext={tenantName}
                />
              </div>
              <div className={classes.meta}>
                {alert.notificationType ? (
                  <Tag
                    compact
                    color="secondary"
                    variant="light"
                    text={getNotificationTypeLabel(alert.notificationType)}
                    noWrap
                  />
                ) : null}
                {alert.createdAt ? (
                  <Table.DateCell date={new Date(alert.createdAt)} />
                ) : null}
              </div>
            </div>
          </Clickable>
        );
      })}
    </div>
  );
};
